import {
  OldSettlement,
  OldUnit,
  OldWorkorderProgress,
  Recnum,
  Settlement,
  Unit,
  WorkorderProgress,
} from '../../types/common';
import { DTO } from '../../utilities/Dto';

// Re-export for backwards compatibility
export type { Settlement, WorkorderProgress };

/** Workorder type as exposed by the api */
export interface Workorder {
  // Workorder fields (read-only)
  /** wbon.recnum (BIGINT) */
  readonly recnum: Recnum;
  /** wbon.id (NUMERIC(8,0)) */
  readonly id: number;
  readonly description: string | null;
  readonly execution_date: Date | null;
  readonly end_date: Date | null;
  readonly progress: WorkorderProgress;

  // Location (read-only)
  readonly location_address: string | null;
  readonly location_doorcode: string | null;
  readonly location_phone: string | null;

  // Customer (read-only)
  readonly customer_name: string | null;
  readonly customer_phone: string | null;
  readonly customer_address: string | null;
  readonly customer_email: string | null;

  // Contact (read-only)
  readonly contact_name: string | null;
  readonly contact_phone: string | null;

  // Assignment (read-only)
  readonly assigned_user_id: number | null;
  readonly werk_id: number;
  readonly work_name: string | null;
  /** From relat.naam */
  readonly technician_name: string | null;
  readonly department: string | null;
  readonly settlement: Settlement | null;
  /** Planner display name from relat.naam (wbon.usr_id -> man.usr_id -> man.relat_id -> relat.naam) */
  readonly creator_name: string | null;

  // Result fields (writable, from werkbon.workorderresult)
  report: string | null;
  internal_note: string | null;
  email: string | null;
  signature_data: string | null;
  completed: boolean;
  signed: boolean;
  date_completed: Date | null;
  travelfee: boolean;
  followup: boolean;
  pdf_task: string;

  // Day planning fields (writable)
  planned_arrive_time: string | null;
  sort_order: number | null;
  route_option: 'direct' | 'via_hq' | null;

  // Route ordering clarity fields (writable)
  /** Source of sort_order: 'auto' (optimized by system), 'manual' (user-defined), or null (unsorted) */
  sort_order_source: 'auto' | 'manual' | null;
  /** SHA-256 hash of routing inputs for change detection */
  routing_key: string | null;
  /** Timestamp when routing_key was last calculated or route was last modified */
  routing_timestamp: Date | null;
}

export interface WorkorderLabor {
  readonly recnum: Recnum;
  readonly workorder_id: Recnum;
  readonly user_id: number;
  date_start: Date;
  date_stop: Date;
  break: number | null;
  dayfee: boolean;
  readonly locked: boolean;
}

/** Labor entry with embedded workorder context (from PostgREST resource embedding) */
// ???: do we need this here? is it not frontend specific
export interface WorkorderLaborWithContext extends WorkorderLabor {
  readonly workorder: {
    readonly id: number;
    readonly description: string | null;
    readonly customer_name: string | null;
    readonly completed: boolean;
  };
}

export type WorkorderMaterialSource = 'pre' | 'manual';

export interface WorkorderMaterial {
  readonly recnum: Recnum;
  readonly workorder_id: Recnum;
  text: string | null;
  quantity: number | null;
  unit: Unit | null;
  readonly locked: boolean;
  readonly source: WorkorderMaterialSource;
}

// DTO Parsers

export function parseWorkorderDto(workorder: DTO<Workorder>): Workorder {
  return {
    ...workorder,
    execution_date: workorder.execution_date
      ? new Date(workorder.execution_date)
      : null,
    end_date: workorder.end_date ? new Date(workorder.end_date) : null,
    date_completed: workorder.date_completed
      ? new Date(workorder.date_completed)
      : null,
    routing_timestamp: workorder.routing_timestamp
      ? new Date(workorder.routing_timestamp)
      : null,
  };
}

export function parseWorkorderLaborDto(
  labor: DTO<WorkorderLabor>,
): WorkorderLabor {
  return {
    ...labor,
    date_start: new Date(labor.date_start),
    date_stop: new Date(labor.date_stop),
  };
}

export function parseWorkorderLaborWithContextDto(
  labor: DTO<WorkorderLaborWithContext>,
): WorkorderLaborWithContext {
  return {
    ...labor,
    date_start: new Date(labor.date_start),
    date_stop: new Date(labor.date_stop),
  };
}

export function parseWorkorderMaterialDto(
  material: DTO<WorkorderMaterial>,
): WorkorderMaterial {
  return material;
}

// Editable fields for updates

export type EditableWorkorderFields = Partial<
  Pick<
    Workorder,
    | 'report'
    | 'internal_note'
    | 'email'
    | 'signature_data'
    | 'completed'
    | 'signed'
    | 'date_completed'
    | 'travelfee'
    | 'followup'
    | 'planned_arrive_time'
    | 'sort_order'
    | 'route_option'
  >
>;

export type EditableWorkorderLaborFields = Partial<
  Pick<WorkorderLabor, 'date_start' | 'date_stop' | 'break' | 'dayfee'>
>;

export type NewWorkorderLabor = Pick<
  WorkorderLabor,
  'workorder_id' | 'date_start' | 'date_stop'
> &
  Partial<Pick<WorkorderLabor, 'break' | 'dayfee'>>;

export type EditableWorkorderMaterialFields = Partial<
  Pick<WorkorderMaterial, 'text' | 'quantity' | 'unit'>
>;

export type NewWorkorderMaterial = Pick<WorkorderMaterial, 'workorder_id'> &
  Partial<Pick<WorkorderMaterial, 'text' | 'quantity' | 'unit'>>;

// =============================================================================
// PDF Task Types (for legacy PDF generation compatibility)
// =============================================================================

/**
 * Labor entry as stored in pdf_task JSON snapshot.
 */
export type WorkorderPdfTaskLabor = DTO<
  Omit<WorkorderLabor, 'workorder_id' | 'break'>
> & {
  workorder_id: string | number;
  break: string | number;
};

/**
 * Material entry as stored in pdf_task JSON snapshot.
 * Unit stored as string for legacy compatibility.
 */
export type WorkorderPdfTaskMaterial = Omit<
  WorkorderMaterial,
  'source' | 'unit' | 'workorder_id' | 'quantity'
> & {
  unit: OldUnit | null;
  workorder_id: string | number;
  quantity: string | number;
};

/**
 * PDF task snapshot structure for workorder PDF generation.
 * This structure is stored in workorderresult.pdf_task as JSON and must
 * remain backwards compatible with the legacy PDF generator in werkbon.
 */
export type WorkorderPdfTask = DTO<
  Omit<
    Workorder,
    | 'settlement'
    | 'progress'
    | 'id'
    | 'planned_arrive_time'
    | 'sort_order'
    | 'route_option'
    | 'assigned_user_id'
    | 'completed'
  >
> & {
  // these are altered
  // Some numbers were, being stored as strings. were allowing both now
  /** wbon.id (NUMERIC(8,0)) */
  id: string | number;
  /** Settlement as string for legacy compatibility */
  settlement: OldSettlement | null;
  /** Settlement as string for legacy compatibility */
  progress: OldWorkorderProgress;
  /** cloud_usr_id => from assigned_user_id */
  cloud_usr_id: string | number;
  /** completed was being stored as 'J'/'N' */
  completed: boolean | 'J' | 'N';

  // these are added
  /** Materials snapshot */
  material: WorkorderPdfTaskMaterial[];
  /** Labor snapshot */
  labor: WorkorderPdfTaskLabor[];
  /** Customer signature data (base64) */
  signature: string | null;
};
